import axios from "axios";
import cn from "classnames";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  AudioMetadataType,
  CaseScore,
  CaseStatus,
  CaseStatusType,
  Case as CaseType,
  ChecklistData,
  ChecklistTemplate,
  PendingSuggestionsChanges,
  PreferenceStatuteStateType,
  RedactionType,
  StatuteType,
  PolicyReference,
  Suggestion,
} from "../db/schema";
import { USER_ROLES, useUserRoles } from "../utilities/roleUtils";
import Cases from "./Cases/Cases";
import RoleBasedAccess from "./common/RoleBasedAccess";
import Editor from "./Editor";
import ResizeHandle from "./common/ResizeHandle";
import { useResizablePanel } from "../hooks/useResizablePanel";
import { pollProcessingStatus } from "./Menu/Menu";
import { cleanupCaseToast, hasCaseToast } from "../services/audioProcessingService";

interface CasesLayoutProps {
  setSelectedCaseId: React.Dispatch<React.SetStateAction<string>>;
  setCaseContent: React.Dispatch<
    React.SetStateAction<{ data: string } | undefined>
  >;
  setHasCases: React.Dispatch<React.SetStateAction<boolean>>;
  setCaseScore: React.Dispatch<React.SetStateAction<CaseScore | undefined>>;
  setCaseSuggestions: React.Dispatch<
    React.SetStateAction<Suggestion[] | undefined>
  >;
  setCaseAudioUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
  caseSuggestions: Suggestion[] | undefined;
  newCase: CaseType | null;
  selectedCaseId: string;
  caseContent: { data: string } | undefined;
  hasCases: boolean;
  caseScore: CaseScore | undefined;
  caseAudioUrl?: string;
  isMobileCase: boolean;
  setIsMobileCase: (value: boolean) => void;
  caseStatus: (typeof CaseStatus.enumValues)[number];
  setCaseStatus: (status: (typeof CaseStatus.enumValues)[number]) => void;
  caseStatutes: StatuteType[];
  setCaseStatutes: React.Dispatch<React.SetStateAction<StatuteType[]>>;
  casePolicyReferences: PolicyReference[];
  setCasePolicyReferences: React.Dispatch<React.SetStateAction<PolicyReference[]>>;
  caseRedactions: RedactionType[];
  setCaseRedactions: React.Dispatch<React.SetStateAction<RedactionType[]>>;
  caseTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setCaseTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  supplementalTranscription: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null;
  setSupplementalTranscription: React.Dispatch<
    React.SetStateAction<{
      task: string;
      language: string;
      duration: number;
      text: string;
      words: {
        word: string;
        start: number;
        end: number;
      }[];
    } | null>
  >;
  activeTab: "narrative" | "transcription";
  setActiveTab: React.Dispatch<
    React.SetStateAction<"narrative" | "transcription">
  >;
  caseAudioMetadata: AudioMetadataType | null;
  setCaseAudioMetadata: React.Dispatch<
    React.SetStateAction<AudioMetadataType | null>
  >;
  pendingSuggestionsChanges: PendingSuggestionsChanges | null;
  setPendingSuggestionsChanges: React.Dispatch<
    React.SetStateAction<PendingSuggestionsChanges | null>
  >;
  setAudioControlsRef: React.Dispatch<
    React.SetStateAction<{
      rewind: (seconds: number) => void;
      fastForward: (seconds: number) => void;
      play: () => void;
      pause: () => void;
    } | null>
  >;
  checklist: {
    template: ChecklistTemplate;
    data: ChecklistData;
    completed_at?: string;
    created_at?: string;
    updated_at?: string;
  } | null;
  setChecklist: React.Dispatch<
    React.SetStateAction<{
      template: ChecklistTemplate;
      data: ChecklistData;
      completed_at?: string;
      created_at?: string;
      updated_at?: string;
    } | null>
  >;
  caseFiles: any[];
  setCaseFiles: React.Dispatch<React.SetStateAction<any[]>>;
  discardedCaseId: string | null;
  onCaseError?: (caseId: string, shouldSelectNext: boolean) => void;
  erroredCase?: { caseId: string; shouldSelectNext: boolean } | null;
  onCaseDiscarded?: (caseId: string) => void;
}

function CasesLayout({
  setSelectedCaseId,
  setCaseContent,
  setHasCases,
  setCaseScore,
  setCaseSuggestions,
  setCaseAudioUrl,
  caseSuggestions,
  newCase,
  selectedCaseId,
  caseContent,
  hasCases,
  caseScore,
  caseAudioUrl,
  isMobileCase,
  setIsMobileCase,
  caseStatus,
  setCaseStatus,
  caseStatutes,
  setCaseStatutes,
  casePolicyReferences,
  setCasePolicyReferences,
  caseRedactions,
  setCaseRedactions,
  caseTranscription,
  setCaseTranscription,
  supplementalTranscription,
  setSupplementalTranscription,
  activeTab,
  setActiveTab,
  caseAudioMetadata,
  setCaseAudioMetadata,
  pendingSuggestionsChanges,
  setPendingSuggestionsChanges,
  setAudioControlsRef,
  checklist,
  setChecklist,
  caseFiles,
  setCaseFiles,
  discardedCaseId,
  onCaseError,
  erroredCase,
  onCaseDiscarded,
}: CasesLayoutProps) {
  const { id: route_id } = useParams<{ id: string }>();
  const DEBOUNCE_TIME = 500;
  const navigate = useNavigate();
  const [isCaseLoading, setIsCaseLoading] = useState<boolean>(false);
  const [showOutageNotice, setShowOutageNotice] = useState<boolean>(false);
  const [caseOwnerId, setCaseOwnerId] = useState<string>("");
  const [isAiGenerated, setIsAiGenerated] = useState<boolean>(false);
  const location = useLocation();
  const page = location.pathname.split("/")[1];
  const isInitialMount = useRef(true);
  const { checkAuthStatus, reportPreferences } = useAuth();
  const { isOfficer, isRecords } = useUserRoles();
  const { user } = useAuth();

  // Determine if the current user can edit the case
  const canEditCase = useMemo(() => {
    if (!selectedCaseId || !user?.id) return false;

    // Case owner can always edit
    if (caseOwnerId === user.id) return true;

    // Records users can edit cases within their agency (for transcription editing)
    if (isRecords()) return true;

    // Default allow for officers editing their own cases
    return true;
  }, [selectedCaseId, caseOwnerId, user?.id, isRecords]);
  // Refresh just the pending_suggestions_changes blob (and audioMetadata) for
  // the current case. Called after the user accepts or discards a pending
  // review so the overlay/buttons reflect the cleared server state without a
  // full case reload.
  //
  // We refresh BOTH fields because isAddToReportInFlight is derived from
  // caseAudioMetadata.intent — if we only refresh the pending blob, a stale
  // intent (e.g. cached "generating_changes" from when the user refreshed
  // mid-flight) keeps the Add to Report button incorrectly disabled even
  // after the discard.
  const refreshPendingSuggestionsChanges = useCallback(
    async (caseId: string) => {
      try {
        const response = await axios.get(
          `/api/cases/${caseId}/pending-suggestions-changes`,
          { withCredentials: true },
        );
        setPendingSuggestionsChanges(response.data.pending || null);
        if (response.data.audioMetadata !== undefined) {
          setCaseAudioMetadata(response.data.audioMetadata || null);
        }
      } catch (err) {
        console.error("Failed to refresh pending suggestions changes:", err);
      }
    },
    [setPendingSuggestionsChanges, setCaseAudioMetadata],
  );

  // Poll for pending Add-to-Report changes while one is actually in flight.
  // Gating on the persisted intent (set optimistically at kickoff in Editor and
  // authoritatively by the server) means we (re)start polling the moment an add
  // begins — even if the case has been open a while — and keep polling until the
  // persisted changes arrive, instead of giving up after a fixed window started
  // when the case was first opened. This is what lets the review dialog appear
  // without a manual page refresh.
  useEffect(() => {
    if (!selectedCaseId) return;
    // Already have a result — stop. Acting on it (review/discard/retry) resets
    // the pending state and re-runs this effect.
    if (pendingSuggestionsChanges) return;
    const intent = (caseAudioMetadata as any)?.intent;
    const addInFlight =
      intent === "add_to_report" || intent === "generating_changes";
    if (!addInFlight) return;

    // Poll every 5s. There is no fixed give-up window: we stop precisely when
    // the gate above flips — i.e. the persisted changes arrive (pending set) or
    // the server clears the in-flight intent (cancel/cleanup). Each refresh also
    // re-reads audioMetadata.intent, so a server-side resolution stops the loop.
    const POLL_INTERVAL_MS = 5_000;
    const intervalId = setInterval(() => {
      refreshPendingSuggestionsChanges(selectedCaseId);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [
    selectedCaseId,
    pendingSuggestionsChanges,
    caseAudioMetadata,
    refreshPendingSuggestionsChanges,
  ]);

  const handleSelectedCase = useCallback(
    async (caseId: string) => {
      if (!caseId) {
        setSelectedCaseId("");
        setCaseContent(undefined);
        setCaseAudioUrl("");
        setCaseOwnerId("");
        return;
      }
      setIsCaseLoading(true);
      setSelectedCaseId(caseId);
      setIsAiGenerated(false);
      // Reset transcription immediately to prevent showing stale data while loading
      setCaseTranscription(null);
      setSupplementalTranscription(null);
      // Reset Add-to-Report state synchronously too. Otherwise the freshly
      // remounted TiptapEditor (via key={caseId}) sees the OLD case's pending
      // blob / audioMetadata still in props and the Suggestions auto-open
      // useEffect fires for the wrong case, causing the SuggestionsReview
      // dialog to persist into the next case until the async fetch finally
      // overwrites the state.
      setPendingSuggestionsChanges(null);
      setCaseAudioMetadata(null);
      try {
        const response = await axios.get(`/api/cases/${caseId}`, {
          withCredentials: true,
        });
        setCaseContent(response.data.case.content);
        setCaseSuggestions(response.data.suggestions);
        setCaseScore(response.data.score);
        setCaseAudioUrl(response.data.case.audioUrl);
        setCaseStatus(response.data.case.status);
        setCaseStatutes(response.data.case.statutes || []);
        setCaseRedactions(response.data.case.redactions || []);
        setCaseTranscription(response.data.transcription || null);
        setSupplementalTranscription(response.data.supplementalTranscription || null);
        setCaseAudioMetadata(response.data.case.audioMetadata || null);
        setPendingSuggestionsChanges(
          response.data.case.pendingSuggestionsChanges || null,
        );
        setCaseOwnerId(response.data.case.userId || "");
        // Set checklist data if present
        setChecklist(response.data.checklist || null);
        // Set case files (for multi-file transcriptions)
        setCaseFiles(response.data.files || []);
        setActiveTab("narrative");
        navigate(`/${page}/${caseId}`);

        // Check if AI was used for this case (for California disclaimer)
        axios
          .get(`/api/audits/${caseId}/ai-generated`, { withCredentials: true })
          .then((res) => setIsAiGenerated(res.data.aiGenerated))
          .catch(() => {});

        // Start polling if case is processing
        if (response.data.case.status === CaseStatusType.enum.processing) {
          // Check if audioProcessingService already has a toast for this case
          const hasExistingToast = hasCaseToast(caseId);
          pollProcessingStatus(caseId, {
            onSuccess: (caseData) => {
              // Check if the current URL still matches this case
              const currentCaseId = window.location.pathname.split("/").pop();
              // Update state directly without recursively calling handleSelectedCase
              if (currentCaseId === caseData.case.id) {
                setSelectedCaseId(caseData.case.id);
                setCaseContent(caseData.case.content);
                setCaseStatus(caseData.case.status);
                setCaseSuggestions(caseData.suggestions || []);
                setCaseScore(caseData.score || null);
                setCaseTranscription(caseData.transcription || null);
                setCaseStatutes(caseData.case.statutes || []);
                setCaseRedactions(caseData.case.redactions || []);
                setCaseFiles(caseData.files || []);
                // Re-check AI-generated audit now that processing finished —
                // the initial fetch in handleSelectedCase ran before the
                // first_draft audit was written, so the CA SB 524 disclaimer
                // would otherwise stay hidden until the user reselects the case.
                // Only needed for California users (disclaimer is CA-only).
                if (
                  reportPreferences?.statute_state ===
                  PreferenceStatuteStateType.Values.California
                ) {
                  axios
                    .get(`/api/audits/${caseData.case.id}/ai-generated`, {
                      withCredentials: true,
                    })
                    .then((res) => setIsAiGenerated(res.data.aiGenerated))
                    .catch(() => {});
                }
              }
            },
            onError: (error) => {
              console.error("Polling failed:", error);
              // Check if the errored case is currently selected
              const currentCaseId = window.location.pathname.split("/").pop();
              const isCurrentlySelected = currentCaseId === caseId;
              // Remove errored case from list, only select next if it was selected
              onCaseError?.(caseId, isCurrentlySelected);
            },
            // Skip showing toast if audioProcessingService is already managing one
            successMessage: hasExistingToast ? undefined : "Narrative generated",
            cleanupToast: hasExistingToast ? undefined : cleanupCaseToast,
          });
        }
      } catch (error) {
        console.error("Error fetching case:", error);
        checkAuthStatus();
      } finally {
        setIsCaseLoading(false);
      }
    },
    [
      navigate,
      page,
      setCaseContent,
      setCaseScore,
      onCaseError,
      setCaseSuggestions,
      setSelectedCaseId,
      setCaseStatus,
      setCaseStatutes,
      setCaseRedactions,
      setCaseAudioUrl,
      setCaseTranscription,
      setSupplementalTranscription,
      setCaseAudioMetadata,
      setActiveTab,
      checkAuthStatus,
      reportPreferences?.statute_state,
      setChecklist,
      setCaseFiles,
    ]
  );

  const handleEditorChange = useMemo(
    () =>
      debounce(async ({ content, caseId }) => {
        if (
          caseId &&
          content.data !== "<h1></h1>" &&
          content.data !== caseContent?.data
        ) {
          try {
            const response = await axios.patch(
              `/api/cases/${caseId}`,
              { content },
              {
                withCredentials: true,
              }
            );
            if (response.status === 200) {
              setCaseContent(content);
            }
          } catch (error) {
            console.error("Error updating case content:", error);
          }
        }
      }, DEBOUNCE_TIME),
    [caseContent, setCaseContent]
  );

  const editorChangeHandler = useCallback(
    ({ content, caseId }) => {
      if (content.data !== caseContent?.data) {
        handleEditorChange({ content, caseId });
      }
    },
    [handleEditorChange, caseContent]
  );

  const setInitialCaseContentOnPageLoad = useCallback(
    async (caseId: string) => {
      setIsCaseLoading(true);
      // Reset transcription immediately to prevent showing stale data while loading
      setCaseTranscription(null);
      setSupplementalTranscription(null);
      try {
        const response = await axios.get(`/api/cases/${caseId}`, {
          withCredentials: true,
        });
        setCaseContent(response.data.case.content);
        setCaseSuggestions(response.data.suggestions);
        setCaseScore(response.data.score);
        setCaseAudioUrl(response.data.case.audioUrl || "");
        setCaseStatus(response.data.case.status);
        setCaseStatutes(response.data.case.statutes || []);
        setCaseRedactions(response.data.case.redactions || []);
        setCaseTranscription(response.data.transcription || null);
        setSupplementalTranscription(response.data.supplementalTranscription || null);
        setCaseAudioMetadata(response.data.case.audioMetadata || null);
        setPendingSuggestionsChanges(
          response.data.case.pendingSuggestionsChanges || null,
        );
        setCaseOwnerId(response.data.case.userId || "");
        // Initial checklist
        setChecklist(response.data.checklist || null);
        // Initial case files
        setCaseFiles(response.data.files || []);

        // Start polling if case is processing
        if (response.data.case.status === CaseStatusType.enum.processing) {
          // Check if audioProcessingService already has a toast for this case
          const hasExistingToast = hasCaseToast(caseId);
          pollProcessingStatus(caseId, {
            onSuccess: (caseData) => {
              // Check if the current URL still matches this case
              const currentCaseId = window.location.pathname.split("/").pop();
              if (currentCaseId === caseData.case.id) {
                setCaseContent(caseData.case.content);
                setCaseSuggestions(caseData.suggestions);
                setCaseScore(caseData.score);
                setCaseStatus(caseData.case.status);
                setCaseStatutes(caseData.case.statutes || []);
                setCaseRedactions(caseData.case.redactions || []);
                setCaseTranscription(caseData.transcription || null);
                setCaseAudioMetadata(caseData.case.audioMetadata || null);
              }
            },
            onError: (error) => {
              console.error("Polling failed:", error);
              // Check if the errored case is currently selected
              const currentCaseId = window.location.pathname.split("/").pop();
              const isCurrentlySelected = currentCaseId === caseId;
              // Remove errored case from list, only select next if it was selected
              onCaseError?.(caseId, isCurrentlySelected);
            },
            // Skip showing toast if audioProcessingService is already managing one
            successMessage: hasExistingToast ? undefined : "Narrative generated",
            cleanupToast: hasExistingToast ? undefined : cleanupCaseToast,
          });
        }
      } catch (error) {
        console.error("Error fetching case:", error);
        setCaseContent(undefined);
      } finally {
        setIsCaseLoading(false);
      }
    },
    [
      setCaseContent,
      setCaseScore,
      setCaseSuggestions,
      setCaseAudioUrl,
      setCaseStatus,
      setCaseStatutes,
      setCaseRedactions,
      setCaseTranscription,
      setSupplementalTranscription,
      setCaseAudioMetadata,
      setChecklist,
      setCaseFiles,
    ]
  );

  // Clean up the debounced function when component unmounts
  useEffect(() => {
    return () => {
      handleEditorChange.cancel();
    };
  }, [handleEditorChange]);

  const handleCasesUpdate = useCallback(
    (casesExist: boolean) => {
      setHasCases(casesExist);
      if (!casesExist) {
        setSelectedCaseId("");
        setCaseContent(undefined);
        setCaseOwnerId("");
        setIsMobileCase(false); // Reset mobile case view when no cases exist
      }
    },
    [setCaseContent, setHasCases, setSelectedCaseId, setIsMobileCase]
  );

  // If route id is passed in, load that case
  useEffect(() => {
    if (isInitialMount.current && route_id) {
      setSelectedCaseId(route_id);
      setInitialCaseContentOnPageLoad(route_id);
      setIsMobileCase(true); // Only set to editor view when specific URL ID exists
      isInitialMount.current = false;
    }
  }, [
    route_id,
    setInitialCaseContentOnPageLoad,
    setSelectedCaseId,
    setIsMobileCase,
  ]);

  // Ensure case is selected after Cases component loads if we have a route_id
  useEffect(() => {
    if (route_id && !selectedCaseId && !isCaseLoading) {
      handleSelectedCase(route_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route_id, selectedCaseId, isCaseLoading]);

  const lastNewCaseId = useRef<string | null>(null);

  // Select new case after creation
  useEffect(() => {
    if (newCase && newCase.id !== lastNewCaseId.current) {
      lastNewCaseId.current = newCase.id;
      setSelectedCaseId(newCase.id);
      setCaseContent(newCase.content as { data: string });
      navigate(`/${page}/${newCase.id}`);
      setIsMobileCase(true);

      // For processing cases, fetch full data and start polling
      if (newCase.status === CaseStatusType.enum.processing) {
        handleSelectedCase(newCase.id);
      }
    }
  }, [
    newCase,
    setSelectedCaseId,
    setCaseContent,
    navigate,
    page,
    setIsMobileCase,
    handleSelectedCase,
  ]);

  const casesPanel = useResizablePanel({
    storageKey: "pnai-casesWidth",
    defaultWidth: 288,
    minWidth: 220,
    maxWidth: 560,
    side: "right",
    maxViewportFraction: 0.4,
  });

  // Ensure checklist data is available when entering checklist mode
  useEffect(() => {
    const fetchChecklistIfNeeded = async () => {
      if (
        caseStatus === CaseStatusType.enum.checklist &&
        selectedCaseId
      ) {
        try {
          const response = await axios.get(`/api/cases/${selectedCaseId}`, {
            withCredentials: true,
          });
          setChecklist(response.data.checklist || null);
        } catch (e) {
          console.error("Failed to fetch checklist for case:", e);
        }
      }
    };
    fetchChecklistIfNeeded();
  }, [caseStatus, selectedCaseId, setChecklist]);

  return (
    <>
      <div
        style={{ "--panel-w": `${casesPanel.width}px` } as CSSProperties}
        className={cn(
          "md:w-[var(--panel-w)] md:shrink-0 bg-base-100 border-r",
          "fixed md:relative inset-0 md:inset-auto z-30",
          "transition-transform duration-300",
          isMobileCase && "translate-x-0"
        )}
      >
        <ResizeHandle edge="right" active={casesPanel.isResizing} {...casesPanel.handleProps} />
        {/* Role indicator */}
        {/* <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600 absolute">
          Role: {isOfficer() ? "Officer" : isRecords() ? "Records" : "Unknown"}
        </div> */}
        <Cases
          selectedCaseId={selectedCaseId}
          handleSelectedCase={(caseId) => {
            handleSelectedCase(caseId);
            if (caseId) {
              // Only set to mobile case view if there's a valid case ID
              setIsMobileCase(true);
              setActiveTab("narrative");
            }
          }}
          setCaseContent={setCaseContent}
          newCase={newCase}
          onCasesUpdate={handleCasesUpdate}
          caseContent={caseContent}
          caseStatus={caseStatus}
          isCaseLoading={isCaseLoading}
          page={page}
          discardedCaseId={discardedCaseId}
          erroredCase={erroredCase}
        />
      </div>
      <div
        className={cn(
          "flex-1 flex flex-col overflow-auto",
          "fixed md:static inset-0 z-30 bg-base-100",
          "transition-transform duration-300 overflow-x-hidden",
          isMobileCase ? "translate-x-0" : "translate-x-full md:translate-x-0"
        )}
      >
        <RoleBasedAccess
          allowedRoles={[USER_ROLES.OFFICER, USER_ROLES.RECORDS]}
        >
          <Editor
            caseContent={caseContent}
            caseSuggestions={caseSuggestions}
            caseStatutes={caseStatutes}
            caseRedactions={caseRedactions}
            setCaseRedactions={setCaseRedactions}
            casePolicyReferences={casePolicyReferences}
            caseScore={caseScore}
            caseAudioUrl={caseAudioUrl}
            onChange={editorChangeHandler}
            caseId={selectedCaseId}
            hasCases={hasCases}
            page={page}
            isProcessing={caseStatus === CaseStatusType.enum.processing}
            isCaseLoading={isCaseLoading}
            setCaseStatutes={setCaseStatutes}
            setCaseSuggestions={setCaseSuggestions}
            setCaseScore={setCaseScore}
            caseTranscription={caseTranscription}
            setCaseTranscription={setCaseTranscription}
            supplementalTranscription={supplementalTranscription}
            setSupplementalTranscription={setSupplementalTranscription}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            caseAudioMetadata={caseAudioMetadata}
            setCaseAudioMetadata={setCaseAudioMetadata}
            pendingSuggestionsChanges={pendingSuggestionsChanges}
            onPendingChangesCleared={() => {
              if (selectedCaseId) {
                refreshPendingSuggestionsChanges(selectedCaseId);
              }
            }}
            handleSelectedCase={handleSelectedCase}
            canEditCase={canEditCase}
            caseOwnerId={caseOwnerId}
            setAudioControlsRef={setAudioControlsRef}
            caseStatus={caseStatus}
            checklist={checklist}
            setCaseContent={setCaseContent}
            caseFiles={caseFiles}
            setCaseFiles={setCaseFiles}
            isAiGenerated={isAiGenerated}
            setIsAiGenerated={setIsAiGenerated}
            onCaseDeleted={onCaseDiscarded}
          />
        </RoleBasedAccess>

        {/* Show different content based on role */}
        {!isRecords() && (
          <RoleBasedAccess
            allowedRoles={[USER_ROLES.OFFICER]}
            fallback={
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-base-content/60">
                  <p>Officer access required</p>
                </div>
              </div>
            }
          >
            {/* Officer-specific features can go here if needed */}
            <div></div>
          </RoleBasedAccess>
        )}

        {/* {isRecords() && (
          <RoleBasedAccess 
            allowedRoles={[USER_ROLES.RECORDS]} 
            fallback={
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-base-content/60">
                  <p>Access restricted to Records personnel</p>
                </div>
              </div>
            }
          >
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-base-content/60">
                <h2 className="text-xl font-semibold mb-4">Records Dashboard</h2>
                <p>Records-specific functionality will be implemented here.</p>
                <p className="text-sm mt-2">This view is only visible to Records personnel.</p>
              </div>
            </div>
          </RoleBasedAccess>
        )} */}
      </div>
      {showOutageNotice && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-yellow-50 dark:bg-amber-950/40 border border-yellow-400 dark:border-amber-900 rounded-lg shadow-lg">
          <div className="flex items-start p-4">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-amber-400">
                Service Notice
              </h3>
              <p className="mt-1 text-sm text-yellow-700 dark:text-amber-400">
                Our AI servers are currently experiencing an outage. Case
                generation may not work properly during this time. We’re
                actively working to resolve the issue. In the meantime, if
                possible, please record your audio on a separate device and
                upload it once this notice is no longer visible.
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                type="button"
                className="inline-flex text-yellow-400 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-yellow-50 rounded-md"
                onClick={() => setShowOutageNotice(false)}
              >
                <span className="sr-only">Close</span>
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CasesLayout;
